/* SemBot · l'hook che porta i quattro stati.
 *
 * Il prototipo mostra ogni contenitore in quattro forme oltre a quella piena:
 * caricamento, vuoto, errore e — la più dimenticata — stantìo. Un hook che
 * restituisca solo `data` e `loading` costringe ogni schermata a reinventare
 * gli altri due, e prima o poi una li salta.
 *
 * `letturaA` è il momento in cui il dato è arrivato: serve a scrivere accanto
 * al numero l'ora della lettura. Fuori casa la rete è incerta e un numero
 * senza orario non è un numero su cui si può decidere.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ErroreApi } from './client';

export type StatoDati = 'caricamento' | 'pieno' | 'vuoto' | 'errore';

export interface Richiesta<T> {
  dati: T | null;
  stato: StatoDati;
  errore: ErroreApi | null;
  /** Momento in cui i dati sono arrivati. `null` finché non arrivano. */
  letturaA: Date | null;
  /** `true` quando si sta ricaricando ma i dati vecchi sono ancora a schermo. */
  inAggiornamento: boolean;
  ricarica: () => void;
}

export interface OpzioniRichiesta<T> {
  /** Se `false` la richiesta non parte: per le pagine che aspettano un id. */
  attiva?: boolean;
  /** Decide quando la risposta è "vuota". Predefinito: array di lunghezza 0. */
  eVuoto?: (dati: T) => boolean;
}

function vuotoPredefinito(dati: unknown): boolean {
  if (dati == null) return true;
  if (Array.isArray(dati)) return dati.length === 0;
  if (typeof dati === 'object' && 'data' in dati) {
    const d = (dati as { data: unknown }).data;
    return Array.isArray(d) && d.length === 0;
  }
  return false;
}

export function usaRichiesta<T>(
  chiama: (segnale: AbortSignal) => Promise<T>,
  dipendenze: readonly unknown[],
  opzioni: OpzioniRichiesta<T> = {},
): Richiesta<T> {
  const { attiva = true, eVuoto = vuotoPredefinito } = opzioni;

  const [dati, setDati] = useState<T | null>(null);
  const [stato, setStato] = useState<StatoDati>(attiva ? 'caricamento' : 'vuoto');
  const [errore, setErrore] = useState<ErroreApi | null>(null);
  const [letturaA, setLetturaA] = useState<Date | null>(null);
  const [inAggiornamento, setInAggiornamento] = useState(false);
  const [giro, setGiro] = useState(0);

  /* La funzione arriva quasi sempre come lambda inline: tenerla in un ref
     evita di rilanciare la richiesta a ogni render del chiamante. Le
     dipendenze dichiarate restano l'unico innesco. */
  const chiamaRef = useRef(chiama);
  chiamaRef.current = chiama;

  const hoDati = dati !== null;

  useEffect(() => {
    if (!attiva) return;

    const controllore = new AbortController();
    let vivo = true;

    if (hoDati) setInAggiornamento(true);
    else setStato('caricamento');
    setErrore(null);

    chiamaRef
      .current(controllore.signal)
      .then((risposta) => {
        if (!vivo) return;
        setDati(risposta);
        setLetturaA(new Date());
        setStato(eVuoto(risposta) ? 'vuoto' : 'pieno');
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setErrore(e instanceof ErroreApi ? e : new ErroreApi(0, String(e)));
        setStato('errore');
      })
      .finally(() => {
        if (vivo) setInAggiornamento(false);
      });

    return () => {
      vivo = false;
      controllore.abort();
    };
    /* `hoDati` e `eVuoto` sono deliberatamente fuori: il primo cambierebbe a
       ogni prima risposta rilanciando la richiesta, il secondo è quasi sempre
       una lambda inline. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attiva, giro, ...dipendenze]);

  const ricarica = useCallback(() => setGiro((g) => g + 1), []);

  return { dati, stato, errore, letturaA, inAggiornamento, ricarica };
}

/* --------------------------------------------------------------------------
 * Stantìo: il dato c'è ma non è di adesso.
 * ------------------------------------------------------------------------ */

/** Oltre questi secondi un saldo letto on-chain non è più un dato su cui
 *  decidere: si mostra in grigio, con l'ora della lettura. */
export const SOGLIA_STANTIO_SECONDI = 120;

export function eStantio(letturaA: Date | null, soglia = SOGLIA_STANTIO_SECONDI): boolean {
  if (!letturaA) return false;
  return (Date.now() - letturaA.getTime()) / 1000 > soglia;
}
